import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Space, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useEffect, useMemo, useState } from 'react';

import { getApiErrorMessage, magicFlowApi } from 'src/services';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';
import type { WorkflowActionSchema } from './components/WorkflowTemplateBuilder.types';
import type {
  CreateWorkflowActionInputs,
  MagicFlowOption,
  RuleRow,
  RuleSetRecord,
} from './MagicFlowAdmin.types';
import {
  createEmptyRuleRow,
  parseJson,
  rowsToRuleSet,
  ruleSetToRows,
  serializeRuleRows,
  stringifyJsonPretty,
} from './magicFlowRuleset.utils';

type UseMagicFlowRulesetControllerParams = {
  orgUuid?: string;
  hasOrgSelected: boolean;
  scopedHeaders: Record<string, string | boolean>;
  schemaHeaders: Record<string, string | boolean>;
  templateOptions: MagicFlowOption[];
  templateOverrideOptions: MagicFlowOption[];
  priorityOptions: MagicFlowOption[];
};

export const useMagicFlowRulesetController = ({
  orgUuid,
  hasOrgSelected,
  scopedHeaders,
  schemaHeaders,
  templateOptions,
  templateOverrideOptions,
  priorityOptions,
}: UseMagicFlowRulesetControllerParams) => {
  const queryClient = useQueryClient();

  const [ruleRows, setRuleRows] = useState<RuleRow[]>([]);
  const [lastSavedRuleRowsSnapshot, setLastSavedRuleRowsSnapshot] = useState(
    serializeRuleRows([])
  );
  const [ruleDetailModal, setRuleDetailModal] = useState<{
    open: boolean;
    rowKey: string | null;
  }>({
    open: false,
    rowKey: null,
  });
  const [additionalInputsDraft, setAdditionalInputsDraft] = useState('{}');

  const {
    data: currentRuleSet,
    isLoading: isLoadingCurrentRuleSet,
    isFetching: isFetchingCurrentRuleSet,
  } = useQuery({
    queryKey: ['magicflow-rule-set', orgUuid],
    enabled: hasOrgSelected,
    queryFn: () =>
      magicFlowApi
        .get<{
          rule_set: RuleSetRecord;
        }>('/rule_set', { headers: scopedHeaders })
        .then((res) => res.data.rule_set),
  });

  const { data: schema } = useQuery({
    queryKey: ['magicflow-schema', orgUuid],
    enabled: hasOrgSelected,
    queryFn: () =>
      magicFlowApi
        .get<{
          rule_actions?: Record<string, unknown>;
          workflow_actions?: Record<string, WorkflowActionSchema>;
        }>('/schema', {
          headers: schemaHeaders,
        })
        .then((res) => res.data),
  });

  useEffect(() => {
    const latest = currentRuleSet?.rule_set || { rules: [] };
    const nextRows = ruleSetToRows(latest);
    setRuleRows(nextRows);
    setLastSavedRuleRowsSnapshot(serializeRuleRows(nextRows));
  }, [currentRuleSet]);

  const saveRuleSetMutation = useMutation({
    mutationFn: async ({
      ruleSet,
    }: {
      ruleSet: Record<string, unknown>;
      snapshot: string;
    }) =>
      magicFlowApi.put(
        '/rule_set',
        { rule_set: ruleSet },
        { headers: scopedHeaders }
      ),
    onSuccess: (_, variables) => {
      showToastSuccess('Rule set saved successfully');
      setLastSavedRuleRowsSnapshot(variables.snapshot);
      queryClient.invalidateQueries({
        queryKey: ['magicflow-rule-set', orgUuid],
      });
    },
    onError: (error) => {
      showToastError(
        getApiErrorMessage(error as any) || 'Failed to save rule set'
      );
    },
  });

  const ruleActionTypeOptions = useMemo(() => {
    const actionSchemas = schema?.rule_actions || {};
    return Object.keys(actionSchemas)
      .sort((a, b) => a.localeCompare(b))
      .map((actionType) => ({ label: actionType, value: actionType }));
  }, [schema?.rule_actions]);

  const updateRuleRow = (key: string, patch: Partial<RuleRow>) => {
    setRuleRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, ...patch } : row))
    );
  };

  const deleteRuleRow = (key: string) => {
    setRuleRows((prev) => prev.filter((row) => row.key !== key));
  };

  const addRuleRow = () => {
    const next = createEmptyRuleRow(ruleRows.length);
    setRuleRows((prev) => [...prev, next]);
    setRuleDetailModal({ open: true, rowKey: next.key });
  };

  const ruleEditorColumns: ColumnsType<RuleRow> = [
    {
      title: 'Rule ID',
      width: 240,
      render: (_, row) => <Typography.Text>{row.id || '-'}</Typography.Text>,
    },
    {
      title: 'Name',
      width: 340,
      render: (_, row) => <Typography.Text>{row.name || '-'}</Typography.Text>,
    },
    {
      title: 'Event',
      width: 340,
      render: (_, row) => (
        <Typography.Text className="font-mono">
          {row.event || '-'}
        </Typography.Text>
      ),
    },
    {
      title: 'Actions',
      width: 100,
      fixed: 'right',
      render: (_, row) => (
        <Space size={8}>
          <Button
            size="small"
            onClick={() => setRuleDetailModal({ open: true, rowKey: row.key })}
            disabled={!hasOrgSelected}
            icon={<EditOutlined />}
          />
          <Button
            danger
            size="small"
            onClick={() => deleteRuleRow(row.key)}
            disabled={!hasOrgSelected}
            icon={<DeleteOutlined />}
          />
        </Space>
      ),
    },
  ];

  const editingRule = useMemo(
    () => ruleRows.find((row) => row.key === ruleDetailModal.rowKey) || null,
    [ruleRows, ruleDetailModal.rowKey]
  );

  const actionInputsParse = useMemo(
    () => parseJson(editingRule?.actionInputsJson || '{}'),
    [editingRule?.actionInputsJson]
  );

  const currentActionInputs = useMemo<CreateWorkflowActionInputs>(() => {
    if (actionInputsParse.error || !actionInputsParse.value) {
      return {};
    }
    return actionInputsParse.value as CreateWorkflowActionInputs;
  }, [actionInputsParse.error, actionInputsParse.value]);

  const conditionsParse = useMemo(
    () =>
      editingRule?.conditionsJson?.trim()
        ? parseJson(editingRule.conditionsJson)
        : { value: null, error: null },
    [editingRule?.conditionsJson]
  );

  const additionalInputsParse = useMemo(
    () => parseJson(additionalInputsDraft || '{}'),
    [additionalInputsDraft]
  );

  useEffect(() => {
    const additionalInputs =
      currentActionInputs.workflow_additional_inputs || {};
    setAdditionalInputsDraft(stringifyJsonPretty(additionalInputs));
  }, [editingRule?.key, editingRule?.actionType]);

  const setCurrentActionInputField = (
    field: keyof CreateWorkflowActionInputs,
    value: unknown
  ) => {
    if (!editingRule) return;

    setRuleRows((prev) =>
      prev.map((row) => {
        if (row.key !== editingRule.key) return row;

        const parsed = parseJson(row.actionInputsJson || '{}');
        const nextInputs: Record<string, unknown> = {
          ...((parsed.value as Record<string, unknown>) || {}),
        };
        const shouldDelete =
          value === undefined ||
          value === null ||
          value === '' ||
          (Array.isArray(value) && value.length === 0);

        if (shouldDelete) {
          delete nextInputs[field];
        } else {
          nextInputs[field] = value;
        }

        return { ...row, actionInputsJson: stringifyJsonPretty(nextInputs) };
      })
    );
  };

  const handleSaveRuleSet = () => {
    const hasMissingFields = ruleRows.some(
      (row) =>
        !row.id.trim() ||
        !row.name.trim() ||
        !row.event.trim() ||
        !row.actionId.trim() ||
        !row.actionType.trim()
    );

    if (hasMissingFields) {
      showToastError(
        'Each rule requires id, name, event, action id, and action type.'
      );
      return;
    }

    try {
      const ruleSet = rowsToRuleSet(ruleRows);
      saveRuleSetMutation.mutate({
        ruleSet,
        snapshot: serializeRuleRows(ruleRows),
      });
    } catch (error) {
      showToastError(
        error instanceof Error ? error.message : 'Invalid ruleset row values'
      );
    }
  };

  return {
    addRuleRow,
    ruleRows,
    ruleEditorColumns,
    isLoadingCurrentRuleSet,
    isFetchingCurrentRuleSet,
    hasUnsavedRuleChanges:
      serializeRuleRows(ruleRows) !== lastSavedRuleRowsSnapshot,
    isSavingRuleSet: saveRuleSetMutation.isPending,
    handleSaveRuleSet,
    ruleDetailModal,
    closeRuleDetailModal: () =>
      setRuleDetailModal({ open: false, rowKey: null }),
    editingRule,
    updateRuleRow,
    ruleActionTypeOptions,
    currentActionInputs,
    setCurrentActionInputField,
    templateOptions,
    templateOverrideOptions,
    priorityOptions,
    actionInputsError: actionInputsParse.error,
    conditionsError: conditionsParse.error,
    additionalInputsDraft,
    setAdditionalInputsDraft,
    additionalInputsError: additionalInputsParse.error,
    parseJson,
  };
};
